import {
  hashQueryKey,
  type Query,
  type QueryClient,
  type QueryKey,
} from "react-query";

import type {
  AmpBoardProjectQuery,
  AmpBoardProjectsQuery,
  AmpBoardTaskQuery,
  AmpBoardTasksQuery,
  AmpProjectRowFragment,
  AmpTaskRowFragment,
} from "src/graphql";
import { useAmpBoardProjectQuery, useAmpBoardTaskQuery } from "src/graphql";

// Every query the area reads is named AmpBoard…, so one prefix finds them all.
const BOARD_QUERY_PREFIX = "AmpBoard";

function isBoardQuery(query: Pick<Query, "queryKey">): boolean {
  const [name] = query.queryKey;
  return typeof name === "string" && name.startsWith(BOARD_QUERY_PREFIX);
}

export type BoardQueryName =
  | "AmpBoardTasks"
  | "AmpBoardTaskGroups"
  | "AmpBoardTask"
  | "AmpBoardProjects"
  | "AmpBoardProject"
  | "AmpBoardProjectCovers"
  | "AmpBoardTags"
  | "AmpBoardBlockerOptions"
  | "AmpBoardAgentTurns";

/** Matches the board queries of these names, or every board query when null. */
export function boardQueryMatcher(names: readonly BoardQueryName[] | null) {
  return (query: Pick<Query, "queryKey">) =>
    isBoardQuery(query) &&
    (names === null || names.some((name) => name === query.queryKey[0]));
}

/**
 * Tasks made or moved in this browser, which views show until a page from the
 * server holds them. A task list entry, so every task patch reaches them.
 */
export const CREATED_TASKS_KEY = ["AmpBoardTasks", "created"] as const;

/** Drops the tasks made or moved here that a page from the server now holds, since the server's copy stays current. */
export function dropLoadedCreatedTasks(queryClient: QueryClient) {
  const createdHash = hashQueryKey(CREATED_TASKS_KEY);
  const loaded = new Set(
    queryClient
      .getQueriesData<AmpBoardTasksQuery | undefined>(["AmpBoardTasks"])
      .filter(([key]) => hashQueryKey(key) !== createdHash)
      .flatMap(([, data]) => data?.amp_tasks.map((task) => task.id) ?? []),
  );
  updateCached<AmpBoardTasksQuery>(queryClient, CREATED_TASKS_KEY, (data) => ({
    amp_tasks: data.amp_tasks.filter((task) => !loaded.has(task.id)),
  }));
}

// A row the server hasn't returned yet carries this id prefix until its real id or a refetch arrives.
const PENDING_ID_PREFIX = "pending:";

/** Whether a row was drawn before the server returned it, so it has no id to write to yet. */
export function isPendingId(id: string): boolean {
  return id.startsWith(PENDING_ID_PREFIX);
}

let pendingCount = 0;

/** A fresh id for a row drawn before the server returns it. */
export function newPendingId(): string {
  pendingCount += 1;
  return `${PENDING_ID_PREFIX}${pendingCount}`;
}

/**
 * Runs an update over the cached queries of one name, or one exact key,
 * skipping any still loading so a patch never stands in for their data.
 */
export function updateCached<T>(
  queryClient: QueryClient,
  nameOrKey: string | QueryKey,
  update: (data: T) => T,
) {
  const queries =
    typeof nameOrKey === "string"
      ? queryClient
          .getQueryCache()
          .findAll({ predicate: (query) => query.queryKey[0] === nameOrKey })
      : queryClient.getQueryCache().findAll(nameOrKey, { exact: true });
  for (const query of queries) {
    const data = query.state.data as T | undefined;
    if (data !== undefined)
      queryClient.setQueryData(query.queryKey, update(data));
  }
}

/**
 * Runs `update` over the board's cache and returns how to put back the queries
 * it changed. Coarser than a task patch's undo, so it suits one-at-a-time settings.
 */
export function revertableUpdate(
  queryClient: QueryClient,
  update: () => void,
): () => void {
  const before = queryClient.getQueriesData({ predicate: isBoardQuery });
  update();
  const changed = before.filter(
    ([key, data]) => queryClient.getQueryData(key) !== data,
  );
  return () => {
    for (const [key, data] of changed) queryClient.setQueryData(key, data);
  };
}

// A task's project is patched through its project, never directly.
export type TaskPatch = Partial<
  Omit<AmpTaskRowFragment, "id" | "__typename" | "project">
>;

/** A patch, or one computed from each cached copy of the task. */
export type TaskUpdate = TaskPatch | ((task: AmpTaskRowFragment) => TaskPatch);

function applyUpdate<T extends AmpTaskRowFragment>(
  task: T,
  update: TaskUpdate,
): T {
  return { ...task, ...(typeof update === "function" ? update(task) : update) };
}

/** Applies an update to one task in a list, or drops it when the update is null. */
export function patchTaskList(
  data: AmpBoardTasksQuery,
  taskId: string,
  update: TaskUpdate | null,
): AmpBoardTasksQuery {
  return {
    ...data,
    amp_tasks: update
      ? data.amp_tasks.map((task) =>
          task.id === taskId ? applyUpdate(task, update) : task,
        )
      : data.amp_tasks.filter((task) => task.id !== taskId),
  };
}

/** Applies an update to a task page's task or one of its subtasks; null drops the subtask. */
export function patchTaskDetail(
  data: AmpBoardTaskQuery,
  taskId: string,
  update: TaskUpdate | null,
): AmpBoardTaskQuery {
  const task = data?.amp_tasks_by_pk;
  if (!task) return data;
  if (task.id === taskId) {
    return update
      ? { ...data, amp_tasks_by_pk: applyUpdate(task, update) }
      : data;
  }
  if (!task.subtasks.some((subtask) => subtask.id === taskId)) return data;
  return {
    ...data,
    amp_tasks_by_pk: {
      ...task,
      subtasks: update
        ? task.subtasks.map((subtask) =>
            subtask.id === taskId ? applyUpdate(subtask, update) : subtask,
          )
        : task.subtasks.filter((subtask) => subtask.id !== taskId),
    },
  };
}

/** Gives a row drawn before the server answered its real id, so it can be written to at once. */
export function swapPendingId<T extends { id: string }>(
  rows: T[],
  pendingId: string,
  id: string,
): T[] {
  if (!rows.some((row) => row.id === pendingId)) return rows;
  // A refetch that already brought the real row leaves the stand-in a duplicate.
  if (rows.some((row) => row.id === id)) {
    return rows.filter((row) => row.id !== pendingId);
  }
  return rows.map((row) => (row.id === pendingId ? { ...row, id } : row));
}

// Only the fields a person edits; the workflow and counts come from the server.
export type ProjectPatch = Partial<
  Pick<
    AmpProjectRowFragment,
    | "title"
    | "description"
    | "status"
    | "owners"
    | "members"
    | "start_at"
    | "due_at"
    | "icon"
    | "deleted_at"
    | "brand_profile_id"
  >
>;

export function patchProjectList(
  data: AmpBoardProjectsQuery,
  projectId: string,
  patch: ProjectPatch,
): AmpBoardProjectsQuery {
  return {
    ...data,
    amp_projects: data.amp_projects.map((project) =>
      project.id === projectId ? { ...project, ...patch } : project,
    ),
  };
}

export function patchProjectDetail(
  data: AmpBoardProjectQuery,
  projectId: string,
  patch: ProjectPatch,
): AmpBoardProjectQuery {
  const project = data?.amp_projects_by_pk;
  if (!project || project.id !== projectId) return data;
  return { ...data, amp_projects_by_pk: { ...project, ...patch } };
}

/** Tasks keep a project's name and icon, so renaming it renames them too. */
export function patchTasksOfProject(
  data: AmpBoardTasksQuery,
  projectId: string,
  patch: ProjectPatch,
): AmpBoardTasksQuery {
  const { title, icon } = patch;
  if (title === undefined && icon === undefined) return data;
  return {
    ...data,
    amp_tasks: data.amp_tasks.map((task) =>
      task.project?.id === projectId
        ? {
            ...task,
            project: {
              ...task.project,
              ...(title === undefined ? {} : { title }),
              ...(icon === undefined ? {} : { icon }),
            },
          }
        : task,
    ),
  };
}

/** A row's current values for the fields a patch changes, to undo it. */
export function previousValues<T extends object, P extends Partial<T>>(
  row: T,
  patch: P,
): P {
  const previous: Partial<T> = {};
  for (const key of Object.keys(patch) as (keyof P & keyof T)[]) {
    previous[key] = row[key];
  }
  return previous as P;
}

/** The part of an undo that still applies: a field goes back only while it holds what the write set. */
export function unchangedSince<T extends object, P extends Partial<T>>(
  row: T,
  patch: P,
  previous: P,
): P {
  const undo: Partial<P> = {};
  for (const key of Object.keys(previous) as (keyof P & keyof T)[]) {
    if ((row[key] as unknown) === patch[key]) undo[key] = previous[key];
  }
  return undo as P;
}

/**
 * A task page drawn from the list row while the full task loads, so opening
 * one from a list shows its title and properties at once.
 */
export function taskDetailPlaceholder(
  task: AmpTaskRowFragment,
): AmpBoardTaskQuery {
  return {
    subtask_links: [],
    activity: [],
    amp_tasks_by_pk: {
      ...task,
      reviews: [],
      parent: null,
      subtasks: [],
      // The row has no brand; the task query fills it in.
      project: task.project
        ? { ...task.project, deleted_at: null, brand_profile_id: null }
        : null,
      links: [],
      blocks: [],
    },
  };
}

/** A project page drawn from its list row while the full project loads. */
export function projectDetailPlaceholder(
  project: AmpProjectRowFragment,
): AmpBoardProjectQuery {
  return {
    activity: [],
    task_links: [],
    amp_projects_by_pk: {
      ...project,
      // The row has no brand; the project query fills it in.
      brand_profile_id: null,
      created_by_user: null,
      links: [],
    },
  };
}

type Positioned = { id: string; position: number };

/**
 * The positions a group takes when a task is dropped into it at an index, only for tasks that move.
 * Positions are integers, so the group is renumbered from its lowest position.
 */
export function reorderPositions(
  group: Positioned[],
  moved: Positioned,
  index: number,
): Positioned[] {
  const rest = group.filter((task) => task.id !== moved.id);
  const clamped = Math.max(0, Math.min(index, rest.length));
  const ordered = [...rest.slice(0, clamped), moved, ...rest.slice(clamped)];
  const base = Math.min(...ordered.map((task) => task.position));
  return ordered.flatMap((task, offset) =>
    task.position === base + offset
      ? []
      : [{ id: task.id, position: base + offset }],
  );
}

/** A manual move: the task takes `to`, and the project's tasks from `from` through `until` move by `by`. */
export type TaskMove = { to: number; from: number; until: number; by: number };

/**
 * The move that puts a task just before or after a neighbor by shifting only
 * the positions between them, so it needs none of the other tasks loaded.
 * Null when the two share a position, which no shift can order.
 */
export function taskMove(
  position: number,
  neighbor: number,
  isAfter: boolean,
): TaskMove | null {
  if (position === neighbor) return null;
  const goal = neighbor + (isAfter ? 0.5 : -0.5);
  if (goal > position) {
    const to = Math.floor(goal);
    return { to, from: position + 1, until: to, by: -1 };
  }
  const to = Math.ceil(goal);
  return { to, from: to, until: position - 1, by: 1 };
}

/**
 * Where a new task lands, as the server places it: just past the last loaded task of its project, or subtask of its parent.
 * Positions are 32-bit integers on the server, so a stand-in must be one too for a move beside it to save.
 */
export function nextTaskPosition(
  queryClient: QueryClient,
  {
    projectId,
    parentTaskId,
  }: { projectId: string; parentTaskId: string | null },
): number {
  const siblings = parentTaskId
    ? (queryClient.getQueryData<AmpBoardTaskQuery>(
        useAmpBoardTaskQuery.getKey({ id: parentTaskId }),
      )?.amp_tasks_by_pk?.subtasks ?? [])
    : queryClient
        .getQueriesData<AmpBoardTasksQuery | undefined>(["AmpBoardTasks"])
        .flatMap(([, data]) => data?.amp_tasks ?? [])
        .filter(
          (task) =>
            task.project_id === projectId && task.parent_task_id === null,
        );
  return siblings.reduce((last, task) => Math.max(last, task.position), -1) + 1;
}

/** A task's row from whichever board query holds it. */
export function findTask(
  queryClient: QueryClient,
  taskId: string,
): AmpTaskRowFragment | undefined {
  for (const [, data] of queryClient.getQueriesData<
    AmpBoardTasksQuery | undefined
  >(["AmpBoardTasks"])) {
    const task = data?.amp_tasks.find((candidate) => candidate.id === taskId);
    if (task) return task;
  }
  const detail = queryClient.getQueryData<AmpBoardTaskQuery>(
    useAmpBoardTaskQuery.getKey({ id: taskId }),
  )?.amp_tasks_by_pk;
  if (detail) return detail;
  // A subtask is listed only on its parent's page.
  for (const [, data] of queryClient.getQueriesData<
    AmpBoardTaskQuery | undefined
  >(["AmpBoardTask"])) {
    const subtask = data?.amp_tasks_by_pk?.subtasks.find(
      (candidate) => candidate.id === taskId,
    );
    if (subtask) return subtask;
  }
  return undefined;
}

export function findProject(queryClient: QueryClient, projectId: string) {
  for (const [, data] of queryClient.getQueriesData<
    AmpBoardProjectsQuery | undefined
  >(["AmpBoardProjects"])) {
    const project = data?.amp_projects.find(
      (candidate) => candidate.id === projectId,
    );
    if (project) return project;
  }
  return (
    queryClient.getQueryData<AmpBoardProjectQuery>(
      useAmpBoardProjectQuery.getKey({ id: projectId }),
    )?.amp_projects_by_pk ?? undefined
  );
}

export type CachedProject = NonNullable<ReturnType<typeof findProject>>;
